import {
  APPOINTMENT_CHANNEL,
  APPOINTMENT_STATUS,
  MODES_OF_APT,
  TYPES_OF_APT,
} from "@elixir/types";
import {
  cancelAppointmentReminders,
  createAppointment,
  getIdb,
  getMe,
  sendAppointmentCommunication,
  updateAppointment,
} from "../../utils/bridges/methods";
import { logAndGetErrorResponse } from "../error";
import { ControllerReturnType } from "../types";
import {
  convertDateToFirestoreTimestamp,
  isoStringToTimestamp,
} from "../../firestore/utils";
import { performFollowupActions } from "./alter-followup-date";
import getB from "../../utils/bridges/EkaBridge";
import dayjs from "dayjs";
import { MESSAGE_TYPE } from "../../utils/bridges/events/types";
import {
  trackAppointmentActivity,
  AptActivityType,
} from "./appointment-activity";

declare global {
  interface Window {
    endVisit: typeof endVisit;
  }
}

export default async function endVisit({
  aid,
  visit,
  pid,
  doctor,
  follow_up,
  prescription_id,
  prescription_url,
  status = !prescription_id
    ? APPOINTMENT_STATUS.COMPLETED_NO_PRESCRIPTION
    : APPOINTMENT_STATUS.COMPLETED,
}: {
  aid: string;
  status?: APPOINTMENT_STATUS;
  visit?: { st: string; et: string };
  pid: { oid: string };
  doctor: { oid: string; clinic: string };
  follow_up?: string;
  prescription_id?: string;
  prescription_url?: string;
}): Promise<ControllerReturnType> {
  try {
    const {
      st: visit_start = new Date().toISOString(),
      et: visit_end = new Date().toISOString(),
    } = visit || {};
    const { oid: patient_oid } = pid || {};
    const { oid: doctor_oid, clinic: clinic_id } = doctor || {};
    const myProfile = getMe();
    const [patientData] = (await getB().getPatientsByIds([patient_oid])) || [];

    const appointment = await getIdb().appointments.get(aid);

    if (appointment) {
      await updateAppointment({
        data: {
          status: status,
          visit_start: isoStringToTimestamp(visit_start),
          visit_end: isoStringToTimestamp(visit_end),
          patient_oid,
          doctor_oid,
          clinic_id,
          prescription_id,
          prescription_url,
        },
        aid,
      });
    } else {
      await createAppointment({
        data: {
          date: convertDateToFirestoreTimestamp(
            dayjs(visit_start).startOf("day").toDate()
          ),
          doctor_oid: doctor_oid,
          aid,
          patient_oid,
          patient_uuid: patientData.uuid!,
          clinic_id,
          channel: APPOINTMENT_CHANNEL.OFFLINE,
          status,
          full_date: convertDateToFirestoreTimestamp(new Date(visit_start)),
          start_time: convertDateToFirestoreTimestamp(new Date(visit_start)),
          end_time: convertDateToFirestoreTimestamp(new Date(visit_end)),
          visit_start: convertDateToFirestoreTimestamp(new Date(visit_start)),
          visit_end: convertDateToFirestoreTimestamp(new Date(visit_end)),
          prescription_id,
          prescription_url,
          follow_up: follow_up
            ? convertDateToFirestoreTimestamp(new Date(follow_up))
            : null,
          bid: myProfile.business.id,
          tags: [],
          archive: false,
          comm_meta: [],
          expires_at: convertDateToFirestoreTimestamp(
            dayjs(visit_start).add(1, "year").toDate()
          ),
          receipts: [],
          apt_fields: [],
          assessments: {},
          mode: MODES_OF_APT.IN_CLINIC,
          visit_type: TYPES_OF_APT.REGULAR,
        },
        aid,
      });
    }

    performFollowupActions({
      appointment: {
        aid,
        clinic_id,
        business_id: myProfile.business.id,
        doctor_oid,
        patient: { uuid: patientData.uuid!, oid: patient_oid },
        follow_up,
      },
    });

    sendAppointmentCommunication({
      aid,
      data: { msg_type: MESSAGE_TYPE.END_VISIT },
    });

    cancelAppointmentReminders({ aid });

    trackAppointmentActivity({
      aid,
      activity: AptActivityType.VISIT_ENDED,
    });
    return { success: true };
  } catch (e) {
    return logAndGetErrorResponse(e);
  }
}

window.endVisit = endVisit;
